import React, { useState } from "react";
import Calendar from "react-calendar";
import moment from "moment";
import { useLanguage, localeFor } from "../i18n";

const ClockCalendar = ({ clock, jobs }) => {
  const { t, lang } = useLanguage();
  const loc = localeFor(lang);
  // Display-only date text in the viewer's language (date keys stay moment/YYYY-MM-DD).
  const fmtShort = (d) =>
    d.toLocaleString(loc, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const { timeLogs } = clock;
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDayLogs, setSelectedDayLogs] = useState(null);

  const jobMap = jobs.reduce((acc, j) => {
    acc[j.id] = j;
    return acc;
  }, {});
  
// 2️⃣ Filter logs to only this tech’s jobs
const filteredLogs = timeLogs.filter(
  (log) => log.userId === clock.userId
);

// 3️⃣ Group logs by LOCAL day (no UTC shift)
const logsByDay = filteredLogs.reduce((acc, log) => {
  if (!log.clockIn) return acc;

  const dateKey = moment(log.clockIn instanceof Date ? log.clockIn : log.clockIn.toDate()).format("YYYY-MM-DD");
  acc[dateKey] = acc[dateKey] || [];
  acc[dateKey].push(log);
  return acc;
}, {});

// 📍 Jobs for popup (based on selected day)
const selectedDayKey =
  selectedDayLogs?.[0]?.clockIn
    ? moment(
        selectedDayLogs[0].clockIn instanceof Date
          ? selectedDayLogs[0].clockIn
          : selectedDayLogs[0].clockIn.toDate()
      ).format("YYYY-MM-DD")
    : null;

const popupJobsForDay = selectedDayKey
  ? jobs.filter(
      (j) =>
        moment(j.startDate?.toDate ? j.startDate.toDate() : j.startDate)
          .format("YYYY-MM-DD") === selectedDayKey &&
        Array.isArray(j.techAssign) &&
        j.techAssign.some((t) =>
          (t || "")
            .toLowerCase()
            .includes((clock.userName || "").toLowerCase())
        )
    )
  : [];

  
  return (
    <div>
      <h2>{t("Time Log Calendar")}</h2>

      {/* 📅 Calendar */}
      <Calendar
      locale={loc}
  value={currentMonth}
  onChange={setCurrentMonth}
  tileContent={({ date }) => {
          const key = moment(date).format("YYYY-MM-DD");
          const logs = logsByDay[key] || [];
          const todayKey = moment().format("YYYY-MM-DD");
          const isOffDay = logs.some(
            (l) => l.reason && /off/i.test(l.reason)
          );          
          const techName = (clock.userName || "").toLowerCase();

          const jobsForDay = jobs.filter(
            (j) =>
              moment(j.startDate?.toDate ? j.startDate.toDate() : j.startDate)
                .format("YYYY-MM-DD") === key &&
              Array.isArray(j.techAssign) &&
              j.techAssign.some(
                (t) => (t || "").toLowerCase().includes(techName)
              )
          );
          
          
          if (isOffDay) {
            return (
              <div style={{ color: "red", fontWeight: 700, fontSize: "0.75rem" }}>
                {t("OFF")}
              </div>
            );
          }
          
          if (logs.length === 0 && key < todayKey) {
            return (
              <div style={{ color: "red", fontWeight: 600, fontSize: "0.75rem" }}>
                {t("OFF")}
              </div>
            );
          }          

if (logs.length === 0) {
  return null;
}


          return (
            <div
              onClick={() => setSelectedDayLogs(logs)}
              style={{
                fontSize: "0.7rem",
                lineHeight: 1.2,
                cursor: "pointer",
                padding: 2,
              }}
            >
             <div style={{ fontWeight: 700 }}>
             {isOffDay
  ? t("OFF")
  : jobsForDay.length > 0
  ? jobsForDay.map((j) => `${j.siteName} (WO ${j.wo})`).join(", ")
  : t("Working")}

</div>


              {logs.length > 1 && (
                <div style={{ fontSize: "0.65rem", color: "#555" }}>
                  {t("+{n} more", { n: logs.length - 1 })}
                </div>
              )}
            </div>
          );
        }}
      />

      {/* 🧾 POPUP MODAL */}

      {selectedDayLogs && (
        <>
          <div
            className="modal-overlay"
            onClick={() => setSelectedDayLogs(null)}
          />

          <div className="modal">
            <h3>{t("Time Logs")}</h3>

            {/* 📍 ADD SITE / WO HEADER INSIDE THE MODAL */}
            {popupJobsForDay.length > 0 && (
              <div style={{ marginBottom: 12, fontWeight: 700 }}>
                {popupJobsForDay
                  .map((j) => `${j.siteName} (WO ${j.wo})`)
                  .join(", ")}
              </div>
            )}

            {selectedDayLogs.map((log, index) => {
              const job = jobMap[log.jobId];

              return (
                <div
                  key={index}
                  style={{
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: "1px solid #ddd",
                  }}
                >

                  <div>
                    <strong>{t("Clock In")}:</strong>{" "}
                    {log.clockIn
                      ? fmtShort(
                          log.clockIn instanceof Date
                            ? log.clockIn
                            : log.clockIn.toDate()
                        )
                      : "—"}
                  </div>

                  <div>
                    <strong>{t("Clock Out")}:</strong>{" "}
                    {log.clockOut
                      ? fmtShort(log.clockOut.toDate())
                      : "—"}
                  </div>

                  <div>
                    <strong>{t("Hours Worked")}:</strong>{" "}
                    {log.duration ? (log.duration / 60).toFixed(2) : "—"}
                  </div>
                </div>
              );
            })}

            <div style={{ textAlign: "right" }}>
              <button
                className="red-button"
                onClick={() => setSelectedDayLogs(null)}
              >
                {t("Close")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClockCalendar;
